import { Flex, IconButton, useBreakpointValue } from "@chakra-ui/react"
import { Document, Video } from "../../../api-clients/assets-api"
import { useBrandedTranslation } from "../../../common/hooks/useBrandedTranslation"
import useModal from "../../../common/hooks/useModal"
import { formatDocuments, formatVideos } from "../../../common/utils/article-utils"
import ButtonWithLeftIcon from "../ButtonWithLeftIcon"
import CustomTooltip from "../CustomTooltip"
import { CPdf, CPlayButtonOutline } from "../Icons"
import DocumentsModal from "./DocumentsModal"
import VideosModal from "./VideosModal"

type AssetsCellProps = {
  documents: Document[]
  videos: Video[]
  articleNumber?: string
  toggleModalBackdropEventListeners?: (hoverDisabled: boolean) => void
}

const AssetsCell = ({ documents, videos, articleNumber, toggleModalBackdropEventListeners }: AssetsCellProps) => {
  const { t } = useBrandedTranslation()
  const isMobile = useBreakpointValue({ base: true, sm: false })

  const hasVideos = videos?.length > 0
  const hasDocuments = documents?.length > 0

  const { Modal: DocumentsDownloadModal, onOpen: onOpenDocumentsModal, onClose: onCloseDocumentsModal } = useModal()
  const { Modal: VideoModal, onOpen: onOpenVideoModal, onClose: onCloseVideoModal } = useModal()

  const handleOpenModal = (modalOpenFun: () => void) => {
    toggleModalBackdropEventListeners?.(true)
    modalOpenFun()
  }

  const formattedDocuments = formatDocuments(documents, t)
  const formattedVideos = formatVideos(videos)

  return (
    <>
      {isMobile ? (
        <Flex flexDirection="column" alignItems="flex-start" display={{ base: "flex", sm: "none" }}>
          {hasDocuments && (
            <ButtonWithLeftIcon
              paddingLeft="0"
              color="black"
              label={t("spdp-document")}
              leftIcon={<CPdf />}
              onClick={() => handleOpenModal(onOpenDocumentsModal)}
            />
          )}
          {hasVideos && (
            <ButtonWithLeftIcon
              paddingLeft="0"
              color="black"
              label={t("spdp-video")}
              leftIcon={<CPlayButtonOutline />}
              onClick={() => handleOpenModal(onOpenVideoModal)}
            />
          )}
        </Flex>
      ) : (
        <Flex display={{ base: "none", sm: "flex" }} gap="2">
          {hasDocuments && (
            <CustomTooltip label={t("spdp-document")}>
              <IconButton
                aria-label="Article documents"
                _hover={{ backgroundColor: "gray.300", _active: { backgroundColor: "gray.900" } }}
                color="black"
                variant="secondary"
                icon={<CPdf />}
                onClick={() => handleOpenModal(onOpenDocumentsModal)}
              />
            </CustomTooltip>
          )}
          {hasVideos && (
            <CustomTooltip label={t("spdp-video")}>
              <IconButton
                aria-label="Article videos"
                _hover={{ backgroundColor: "gray.300", _active: { backgroundColor: "gray.900" } }}
                color="black"
                variant="secondary"
                icon={<CPlayButtonOutline />}
                onClick={() => handleOpenModal(onOpenVideoModal)}
              />
            </CustomTooltip>
          )}
        </Flex>
      )}

      <DocumentsModal
        Modal={DocumentsDownloadModal}
        articleNumber={articleNumber ?? ""}
        documents={formattedDocuments}
        onClose={() => {
          onCloseDocumentsModal?.()
          toggleModalBackdropEventListeners?.(false)
        }}
      />

      <VideosModal
        Modal={VideoModal}
        articleNumber={articleNumber ?? ""}
        videos={formattedVideos}
        onClose={() => {
          onCloseVideoModal?.()
          toggleModalBackdropEventListeners?.(false)
        }}
      />
    </>
  )
}

export default AssetsCell
